import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconEdit, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useDeleteProspect, useProspectsList } from '../../queries/prospects';
import { ProspectFormDrawer } from './ProspectFormDrawer';
import { useProjectStore } from '../../project/project-store';
import { ApiError } from '../../lib/api-error';
import type { Prospect } from '../../types/api';

export function ProspectsListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';
  const canDelete = role === 'owner' || role === 'moderator';

  const [q, setQ] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Prospect | null>(null);

  const filters = useMemo(() => ({ q: q.trim() || undefined, limit: 200 }), [q]);
  const { data: prospects = [], isLoading } = useProspectsList(filters);
  const deleteM = useDeleteProspect();

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Prospects</Title>
        <Text c="dimmed">Pick a project to view its prospects.</Text>
      </Stack>
    );
  }

  function openCreate() {
    setEditTarget(null);
    setDrawerOpen(true);
  }

  function openEdit(p: Prospect) {
    setEditTarget(p);
    setDrawerOpen(true);
  }

  function confirmDelete(p: Prospect) {
    modals.openConfirmModal({
      title: 'Soft-delete this prospect?',
      children: (
        <Text size="sm">
          The prospect will disappear from the picker but audit history is preserved. Entries
          currently referencing it must be cleared first; otherwise the API returns
          <code> prospect_in_use</code>.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteM.mutateAsync(p.id);
          notifications.show({ title: 'Prospect deleted', message: p.name, color: 'pine' });
        } catch (err) {
          notifications.show({
            title: 'Delete failed',
            message: err instanceof ApiError ? err.message : 'Unexpected error',
            color: 'red',
          });
        }
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Prospects</Title>
          <Text c="dimmed" size="sm">
            Project-scoped lookup of named prospects (drilling targets, AOI blocks). Tag entries
            with a prospect so you can filter the runsheet by target.
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New prospect
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search by name"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={prospects}
        idAccessor="id"
        noRecordsText="No prospects yet"
        columns={[
          {
            accessor: 'name',
            title: 'Name',
            render: (p) => (
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {p.name}
                </Text>
                {p.description && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {p.description}
                  </Text>
                )}
              </Stack>
            ),
          },
          {
            accessor: 'entry_count',
            title: 'Entries',
            width: 100,
            textAlign: 'right',
            render: (p) =>
              (p.entry_count ?? 0) > 0 ? p.entry_count : <Text c="dimmed">—</Text>,
          },
          {
            accessor: 'actions',
            title: '',
            width: 90,
            render: (p) => (
              <Group gap={4} justify="flex-end" wrap="nowrap">
                {canWrite && (
                  <Tooltip label="Edit">
                    <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(p)}>
                      <IconEdit size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDelete && (
                  <Tooltip label="Delete">
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => confirmDelete(p)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
      />

      <ProspectFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        prospect={editTarget}
      />
    </Stack>
  );
}
